import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  addDepartmentSchedule,
  deleteDepartmentSchedule,
  setDepartmentSchedule,
} from "../../api/department_schedule";
import { getDoctors } from "../../api/doctors";
import { SCHEDULE_TYPE } from "../../const/enums";
import { clipboardAtom } from "../../store/clipboard";
import { userAtom } from "../../store/user";
import { RegularButton } from "../Button";
import DoctorSelector from "./DoctorSelector";

export default function ScheduleTypeEdit(props) {
  const {
    isNew,
    date,
    obj,
    byDate,
    onClose,
    open,
    canAllDay,
    checkRegister,
    paramScheduleTypeId,
  } = props;

  const getDefaultStartTime = (obj) => {
    if (obj) {
      return obj.start_time;
    }
    if (
      [SCHEDULE_TYPE.ON_DUTY, SCHEDULE_TYPE.ON_CALL].indexOf(
        paramScheduleTypeId,
      ) >= 0
    ) {
      return user.department?.end_work_time;
    }
    return user.department?.start_work_time;
  };

  const getDefaultEndTime = (obj) => {
    if (obj) {
      return obj.end_time;
    }
    if (
      [SCHEDULE_TYPE.ON_DUTY, SCHEDULE_TYPE.ON_CALL].indexOf(
        paramScheduleTypeId,
      ) >= 0
    ) {
      return user.department?.start_work_time;
    }
    return user.department?.end_work_time;
  };

  const user = useRecoilValue(userAtom);
  const [clipboard, setClipboard] = useRecoilState(clipboardAtom);
  const [isDelete, setDelete] = React.useState(false);
  const [scheduleTypeId, setScheduleTypeId] =
    React.useState(paramScheduleTypeId);
  const [userId, setUserId] = React.useState(obj?.user_id ?? "");
  const [startTime, setStartTime] = React.useState(
    dayjs(getDefaultStartTime(obj), "hh:mm"),
  );
  const [endTime, setEndTime] = React.useState(
    dayjs(getDefaultEndTime(obj), "hh:mm"),
  );
  const [note, setNote] = React.useState(obj?.note ?? "");
  const [isSettled, setIsSettled] = React.useState(obj?.is_settled ?? false);
  const [registerError, setRegisterError] = React.useState(null);
  const [showCopiedAlert, setShowCopiedAlert] = React.useState(false);
  const doctor_id_excludes = [];

  const handleSetAm = () => {
    setStartTime(dayjs(user.department?.start_work_time, "hh:mm"));
    setEndTime(dayjs(user.department?.start_break_time, "hh:mm"));
  };

  const handleSetPm = () => {
    setStartTime(dayjs(user.department?.end_break_time, "hh:mm"));
    setEndTime(dayjs(user.department?.end_work_time, "hh:mm"));
  };

  const handleSetAllTime = () => {
    setStartTime(dayjs(user.department?.start_work_time, "hh:mm"));
    setEndTime(dayjs(user.department?.end_work_time, "hh:mm"));
  };

  const handleSetNight = () => {
    setStartTime(dayjs(user.department?.end_work_time, "hh:mm"));
    setEndTime(dayjs(user.department?.start_work_time, "hh:mm"));
  };

  const handleCopy = () => {
    setClipboard({
      scheduleByType: {
        scheduleTypeId: scheduleTypeId,
        userId: userId,
        startTime: startTime,
        endTime: endTime,
        note: note,
        is_settled: isSettled,
      },
    });
    setShowCopiedAlert(true);
    setTimeout(() => {
      setShowCopiedAlert(false);
    }, 2000);
  };

  const handlePaste = () => {
    if (clipboard?.scheduleByType) {
      setScheduleTypeId(clipboard.scheduleByType?.scheduleTypeId ?? "");
      setUserId(clipboard.scheduleByType?.userId ?? "");
      setStartTime(clipboard.scheduleByType?.startTime ?? "");
      setEndTime(clipboard.scheduleByType?.endTime ?? "");
      setNote(clipboard.scheduleByType.note ?? "");
      setIsSettled(clipboard.scheduleByType?.is_settled ?? "");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleChangeUserId = (e) => {
    setUserId(e.target.value);
  };

  const { data, isSuccess } = useQuery({
    queryKey: ["doctors"],
    queryFn: () => getDoctors(),
    throwOnError: true,
  });

  if (isSuccess) {
    if (byDate) {
      for (const v of byDate) {
        for (const doctor of data) {
          if (
            doctor.user.id === v.user_id &&
            v.schedule_type_id === scheduleTypeId &&
            v.is_negative
          ) {
            doctor_id_excludes.push(doctor.id);
            break;
          }
        }
      }
    }
  }

  // doctor selector dialog
  const [openDoctorSelector, setOpenDoctorSelector] = React.useState(false);
  const handleClickDoctorSelectorOpen = () => {
    setOpenDoctorSelector(true);
  };
  const handleCloseDoctorSelector = () => {
    setOpenDoctorSelector(false);
  };
  const handleDoctorSelect = (user_id) => {
    setUserId(user_id);
  };

  return (
    <>
      <DoctorSelector
        key={Date.now()}
        onClose={handleCloseDoctorSelector}
        open={openDoctorSelector}
        date={date}
        select={handleDoctorSelect}
        userId={obj?.user_id ?? ""}
        doctor_id_excludes={doctor_id_excludes}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          onSubmit: (event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const formJson = Object.fromEntries(formData.entries());
            formJson.date = date;
            if (!formJson.is_settled) {
              formJson.is_settled = false;
            }
            formJson.schedule_type_id = scheduleTypeId;
            console.log(`form value: ${JSON.stringify(formJson)}`);

            if (!isDelete) {
              const error = checkRegister(formJson, byDate, obj);
              console.log(`error: ${error}`);
              if (error) {
                setRegisterError(error);
                return;
              }
            }

            if (isNew) {
              console.log("新規");
              addDepartmentSchedule(formJson).then(handleClose);
            } else if (isDelete) {
              formJson.id = obj.id;
              console.log(`削除${JSON.stringify(formJson)}`);
              deleteDepartmentSchedule(formJson).then(handleClose);
            } else {
              formJson.id = obj.id;
              console.log(`変更:${JSON.stringify(formJson)}`);
              setDepartmentSchedule(formJson).then(handleClose);
            }
            //handleClose();
          },
        }}
      >
        <DialogTitle>{date}</DialogTitle>
        <DialogContent sx={{ width: "420px" }}>
          <InputLabel
            id="doctor_label"
            sx={{ display: "flex", alignContent: "center" }}
          >
            医師&nbsp;
            <LaunchIcon
              sx={{ color: "primary.main" }}
              onClick={() => {
                handleClickDoctorSelectorOpen();
              }}
            />
          </InputLabel>
          <Select
            variant="outlined"
            sx={{ marginBottom: "10px" }}
            autoFocus
            required
            labelId="doctor_label"
            id="user_id"
            name="user_id"
            value={userId ?? ""}
            onChange={handleChangeUserId}
            fullWidth
          >
            {data?.map(
              (doctor) =>
                doctor_id_excludes.indexOf(doctor.id) === -1 && (
                  <MenuItem value={doctor.user.id} key={doctor.user.id}>
                    {doctor.full_name}
                  </MenuItem>
                ),
            )}
          </Select>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  id="is_settled"
                  name="is_settled"
                  checked={isSettled}
                  onChange={(e) => setIsSettled(e.target.checked)}
                />
              }
              label="ロック"
            />
            <Stack direction="row" spacing={1} sx={{ margin: "10px 0" }}>
              <Button
                id="copy"
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  height: "36px",
                }}
                onClick={handleCopy}
              >
                コピー
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={!clipboard?.scheduleByType}
                sx={{
                  height: "36px",
                }}
                onClick={handlePaste}
              >
                貼り付け
              </Button>
              <Alert
                severity="success"
                sx={{
                  width: "200px",
                  padding: "0 10px",
                  height: "36px",
                  display: showCopiedAlert ? "flex" : "none",
                }}
              >
                コピーしました
              </Alert>
            </Stack>
            {!obj?.is_negative && (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ marginBottom: "10px" }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      height: "36px",
                    }}
                    onClick={handleSetAm}
                  >
                    午前
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      height: "36px",
                    }}
                    onClick={handleSetPm}
                  >
                    午後
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      height: "36px",
                    }}
                    onClick={handleSetAllTime}
                  >
                    終日
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      height: "36px",
                    }}
                    onClick={handleSetNight}
                  >
                    夜間
                  </Button>
                </Stack>
                <DemoContainer components={["TimePicker"]}>
                  <TimePicker
                    label="開始時刻"
                    name="start_time"
                    clearable
                    ampm={false}
                    value={startTime}
                    onChange={(val) => setStartTime(val)}
                  />
                </DemoContainer>
                <DemoContainer components={["TimePicker"]}>
                  <TimePicker
                    label="終了時刻"
                    name="end_time"
                    clearable
                    ampm={false}
                    value={endTime}
                    onChange={(val) => setEndTime(val)}
                  />
                </DemoContainer>
              </>
            )}
            {registerError && (
              <Typography sx={{ color: "error.main" }}>
                {registerError}
              </Typography>
            )}
            <TextField
              margin="dense"
              id="note"
              name="note"
              label="備考"
              type="text"
              fullWidth
              variant="standard"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>キャンセル</Button>
          <RegularButton
            type="submit"
            variant="contained"
            color="error"
            size="medium"
            sx={{}}
            onClick={() => setDelete(true)}
          >
            削除
          </RegularButton>
          <RegularButton
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            sx={{}}
            onClick={() => setDelete(false)}
          >
            登録
          </RegularButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
